// Slot-aware navigation for the notes surface (2026-08-06 per Nika: "if its
// open in pageslot - it should keep it, otherwise link to page").
//
// The notes UI mounts in TWO hosts: as the real /notes route, and inside the
// home PageSlot window (root._canvasSlot). Every internal navigation (cards,
// section rows, + New note, back, context-menu open/edit) previously did a
// full router navigation, which tore the user OUT of the window. This one
// registered function is the single navigation path for the whole surface:
//   - slot-hosted → update `_canvasSlot` (the PageSlot re-mounts by
//     pathname) and put any #hash on the REAL URL (the editor's hash
//     restore reads window.location.hash — same mechanism the home
//     DocumentsWidget tiles already use)
//   - route-hosted → the normal MWT router navigation
//
// Slot detection is state-based, not structural: the slot shows a notes
// path while the real route is somewhere else (home). No coupling to
// PageSlot internals, and it works from the EAGER sidebar rows too.
import { stripWorkspacePrefix } from '@symbo-ls/workspace-shared/functions/workspacePrefix.js'

const _isNotesPath = (p) =>
  p === '/notes' ||
  p.startsWith('/notes/') ||
  p === '/documents' ||
  p.startsWith('/documents/') ||
  p === '/docs'

// `opts.replace` swaps the current history entry instead of adding one — for
// a move inside the editor (a linked note opened as the note,
// functions/notesRefs.js openNoteRefFull): the editor reads its hash once, so
// Back to an entry of the same page would show the address of one note over
// another.
// `opts.slot` opens the path in the PageSlot even when the slot shows no notes
// path yet — the assistant's main thread opens a doc BESIDE itself this way
// (workspace pageContext.js selectPageItem): a route change closes that thread.
export const notesNavigate = function (path, opts) {
  const el = this
  const root = el.state?.root
  if (!root) return
  const [base, hash] = String(path || '/notes').split('#')

  const slot = String(root._canvasSlot || '').split('?')[0]
  const realPath = stripWorkspacePrefix(
    root.currentPath ?? (typeof window !== 'undefined' ? window.location.pathname : '/')
  )
  const inSlot = !!(opts && opts.slot) || (_isNotesPath(slot) && !_isNotesPath(realPath))

  if (inSlot) {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.hash = hash || ''
      window.history.replaceState(null, '', url.toString())
    }
    root.update({ _canvasSlot: base })
    return
  }

  let rootEl = el
  while (rootEl && !rootEl.routes) rootEl = rootEl.parent
  const router = el.context?.utils?.router
  const replace = !!(opts && opts.replace)
  if (router && rootEl) router(path, rootEl, {}, { pushState: true, replace, scrollToTop: true })
}
